import React, { useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import "../../../../../sass/components/my-documents-page.scss";

import { DocumentsTabs } from "./DocumentsTabs";
import { useDpas } from "../../../hooks/useDataProtectionAssessment";
import { usePrimaryOrganizationId } from "../../../hooks";
import { Article, Download } from "@mui/icons-material";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { CloseableDialogTitle } from "../../../components/dialog/CloseableDialogTitle";
import { Routes } from "../../../root/routes";
import { Breadcrumbs } from "@truevault/aquilo";

export const formatDpaDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const AbandonedDpaRow = ({ dpa }) => {
  const [abandonedNoteDialogOpen, setAbandonedNoteDialogOpen] = useState(false);

  return (
    <>
      <div className="completed-dpa-table-row" key={dpa.id}>
        <div className="font-weight-medium d-flex flex--align-center">
          <>
            {dpa.name}
            {dpa.abandonedNote && dpa.abandonedNote.length > 0 && (
              <div
                className="ml-xs cursor-pointer border d-flex text-t0 flex--align-center"
                style={{ borderRadius: "100px", gap: "7px", padding: "3px 10px" }}
                onClick={() => setAbandonedNoteDialogOpen(true)}
              >
                <Article sx={{ width: "12px", height: "12px" }} />
                Note
              </div>
            )}
          </>
        </div>
        <div className="text-center">{formatDpaDate(dpa.abandonedAt)}</div>
        <div className="text-center">{dpa.abandonedBy}</div>
      </div>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={abandonedNoteDialogOpen}
        onClose={() => setAbandonedNoteDialogOpen(false)}
        className={`get-compilant-dialog confirm-dialog`}
      >
        <CloseableDialogTitle onClose={() => setAbandonedNoteDialogOpen(false)}>
          Deletion Note
        </CloseableDialogTitle>
        <DialogContent>{dpa.abandonedNote}</DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setAbandonedNoteDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DPAPageHistory = () => {
  const orgId = usePrimaryOrganizationId();
  const [dpas, dpasRequest] = useDpas(orgId);

  const completedDpas = (dpas || []).filter((dpa) => dpa.status == "COMPLETED");
  const abandonedDpas = (dpas || []).filter((dpa) => dpa.status == "ABANDONED");

  return (
    <StandardPageWrapper requests={[dpasRequest]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Data Protection Assessments"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        />
      </div>
      <p className="sub-header">Your Assessments</p>
      <DocumentsTabs current="history" />
      {completedDpas.length == 0 && abandonedDpas.length == 0 && (
        <div className="empty-page">
          <img src="/assets/images/stay_compliant/empty-page.svg" alt="add" />
          <p>No Completed Assessments</p>
        </div>
      )}
      {completedDpas.length > 0 && (
        <div className="mt-md">
          <div className="completed-dpa-table-row">
            <div className="text-weight-bold">Name</div>
            <div className="text-weight-bold text-center">Date Completed</div>
            <div className="text-weight-bold text-center">Download</div>
          </div>
          <hr />
          {completedDpas.map((dpa) => (
            <div className="completed-dpa-table-row" key={dpa.id}>
              <div className="font-weight-medium">{dpa.name}</div>
              <div className="text-center">{formatDpaDate(dpa.completedAt)}</div>
              <div className="text-center">
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={ApiRouteHelpers.organization.documents.dataProtectionAssessments.signedPdf(
                    orgId,
                    dpa.id,
                  )}
                >
                  <Download />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
      {abandonedDpas.length > 0 && (
        <div className="mt-xl">
          <div className="completed-dpa-table-row">
            <div className="text-weight-bold">Name</div>
            <div className="text-weight-bold text-center">Date Deleted</div>
            <div className="text-weight-bold text-center">Deleted By</div>
          </div>
          <hr />
          {abandonedDpas.map((dpa) => (
            <AbandonedDpaRow dpa={dpa} key={dpa.id} />
          ))}
        </div>
      )}
    </StandardPageWrapper>
  );
};
